import React, { useState, useEffect, useRef, useCallback } from "react";
import useAxios from "../../hooks/useAxios";
import useCommonActions from "../../hooks/useCommonActions";
import { ContentLoader, PageToolbar, Pagination, TooltipWrapper } from "../common";
import badges from "../../_helpers/badgeHelper";
import helpers from "../../_helpers/common";
import { hasAuthorization } from "../../_helpers/hasAuthorization";
import SearchForm from "./partial/SearchForm";
import CommonList from "../common/CommonList";
import { ServiceEnquirySearchFormState, ServiceEnquiry } from "../../types/interfaces";

const List: React.FC = () => {
  const axiosInstance = useAxios();
  const { changeItemStatus, deleteItem } = useCommonActions();
  const [searchForm, setSearchForm] = useState<ServiceEnquirySearchFormState>({ search_string: "", status: "" });
  const [results, setResults] = useState<ServiceEnquiry[] | null>(null);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10 });
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isInitialRender = useRef(false);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    getListData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  /*Handle pagination data change*/
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ currentPage: page, pageSize });
  };

  const getListData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: any = await axiosInstance({
        url: "service-enquiry/list",
        method: "GET",
        data: {
          page: pagination.currentPage,
          limit: pagination.pageSize,
          ...searchForm,
        },
      });
      // console.log('service-enquiry list response', response);
      setResults(response.data.enquiries.docs);
      setTotalItems(response.data.enquiries.totalDocs);
    } catch (error: any) {
      console.error("Error in api request:", error);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[pagination,searchForm]);

  /*Change selected item status*/
  const changeEnquiryStatus = async (itemid?: string, status?: number) => {
    const newStatus = status === 1 ? 0 : 1;
    const message = `Are you sure you want to ${
      status === 1 ? "deactivate" : "activate"
    } the enquiry?`;
    changeItemStatus(
      itemid,
      newStatus,
      "service-enquiry/update-status",
      message,
      getListData
    );
  };

  /*Delete selected item*/
  const deleteEnquiry = async (itemid?: string) => {
    const message = `Are you sure you want to delete the enquiry?`;
    deleteItem(`service-enquiry/delete/${itemid}`, message, getListData);
  };
  
  
  return (
    <>
      <div className="d-flex justify-content-between">
        {/*Page toolbar Area Start*/}
        <PageToolbar title="Service Enquiry List" />
      </div>
	  <div id="kt_content" className="content flex-column-fluid">
        {/*Search Area Start*/}
        <SearchForm searchForm={searchForm} setSearchForm={setSearchForm} handleSearchClick={getListData}/>
        {/*Search Area End*/}

        {/*Content Area Start*/}
        <div className={`card ${isLoading ? "overlay overlay-block" : ""}`}  style={{ minHeight:"70vh", maxHeight: "70vh" }} >
          <div className="card-body pt-0 pb-0 table-responsive">
		    <table className="table table-hover table-row-bordered table-row-solid gy-4 align-middle fw-bold w-100">
			  <thead
				style={{
				  position: "sticky",
				  top: 0,
				  zIndex: 5,
				  backgroundColor: `${isLoading ? "#BBBCBE" : "white"}`,
				  boxShadow: "0 4px 2px -2px rgba(190, 190, 190, 0.2)",
				}}
			  >
				<tr className="text-start text-gray-800 fw-bold fs-7 text-uppercase gs-0">
				  <th className="min-w-150px">Service Enquiry</th>
				  <th className="min-w-150px">Name</th>
				  <th className="min-w-150px">Email</th>
				  <th className="min-w-70px">Phone No</th>
				  <th className="min-w-270px">Message</th>
				  <th className="min-w-70px">Status</th>
				  <th className="min-w-100px">Created Date</th>
				  <th className="min-w-70px">Actions</th>
				</tr>
			  </thead>
			  <tbody className="fw-semibold text-gray-600">
				{!results || results.length === 0 ? (
				  <tr className="text-center">
					<td colSpan={6}>No Content Found</td>
				  </tr>
				) : (
				  results.map((item) => (
					<tr key={item._id}>
					  <td>{helpers.splitAndCapitalizeFirstWord(item.service, '-') || "N/A"}</td>
					  <td>{item.name || "N/A"}</td>
					  <td>{item.email || "N/A"}</td>
					  <td>{item.phone || "N/A"}</td>
					  <td style={{ maxWidth: "750px" }}>
						  <span	className="d-inline-block">
							{item?.message || "N/A"}
						  </span>
					  </td>
					  <td>
						{badges.eventStatusBadge(
						  item.status ?? 0,
						  item?.statusname ?? ""
						)}
					  </td>
					  <td>
						{helpers.convertDateTime(
						  item?.created_at,
						  "DD MMM YYYY"
						) || "N/A"}
					  </td>
					  <td className="text-end">
						<div className="d-flex justify-content-start">
						  {hasAuthorization("update_status", "enquiry") && (
							<TooltipWrapper
							  title="Update Enquiry Status"
							  placement="top"
							>
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-warning me-2"
								onClick={() =>
								  changeEnquiryStatus(item?._id, item?.status)
								}
							  >
								<i className="ki-outline ki-shield-tick text-warning fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}

						  {hasAuthorization("delete", "enquiry") && (
							<TooltipWrapper title="Delete Enquiry" placement="top">
							  <span
								className="btn btn-sm btn-icon btn-light btn-active-light-danger"
								onClick={() => deleteEnquiry(item?._id)}
							  >
								<i className="ki-outline ki-trash text-danger fs-2"></i>
							  </span>
							</TooltipWrapper>
						  )}
						</div>
					  </td>
					</tr>
				  ))
				)}
			  </tbody>
			  <tfoot></tfoot>
			</table>
		  
		  </div>
          {/*Content Area End*/}
          {/*Pagination Start*/}
          <div
            className={`sticky-bottom bg-white border rounded p-2 shadow-sm ${
              isLoading ? "" : "border-top"
            }`}
            style={{
              bottom: 0,
              zIndex: 2,
              // backgroundColor: `${isLoading ? "#BBBCBE" : "white"}`,
            }}
          >
            {results && results.length > 0 ? (
              <Pagination
                totalItems={totalItems}
                itemsPerPage={pagination.pageSize}
                onPageChange={handlePageChange}
              />
            ) : null}
          </div>
          {/*Pagination End*/}

          {/*Content Loader Start*/}
          {isLoading ? <ContentLoader /> : null}
          {/*Content Loader End*/}
        </div>
      </div>
   </>
  );
};

export default List;
